<template>
    <!-- Componente para comprarar las versiones dentro de la vista del configurador de versiones -->
    <div class="mt-12 container">
        <div
            class="versions-comparator lg:bg-light-brown mt-8 pt-8 pb-12 shadow"
            v-show="showVersions"
        >
            <div class="text-center mb-8">
                <h3 class="font-medium text-xl sm:text-2xl">Compara tu modelo</h3>
                <p class="sm:text-lg">Selecciona un máximo de dos versiones que quieras comparar</p>
            </div>
            <!-- Versión del selector de modelos para dispositivo móviles -->
            <div class="versions-mobile lg:hidden">
                <div
                    class="version bg-light-brown p-4 mb-2 sm:mb-1 flex flex-wrap sm:h-48 w-11/12 mx-auto"
                    v-for="(version, index) in versions"
                    :key="index"
                >
                    <!-- <p></p> -->
                    <div class="order-1 mb-8 sm:mb-0">
                        <label class="checkbox-container">
                            <input
                                type="checkbox"
                                @click="onCheckVersion(index)"
                                :class="'check-' + index"
                            >
                            <span class="checkmark bg-white"></span>
                        </label>
                    </div>
                    <div class="order-3 sm:order-2 sm:w-5/12">
                        <img
                            class="h-40 w-full object-cover"
                            :src="version.colorImage"
                            :alt="version.name"
                        >
                    </div>
                    <div class="order-2 sm:order-3 sm:w-5/12 sm:pl-6 leading-none flex flex-col content-center">
                        <h2 class="block text-xl sm:text-2xl font-medium">{{ version.name }}</h2>
                        <p class="block text-lg mt-2 sm:mt-3">${{ formatPrice(version.price) }} MXN</p>
                    </div>
                </div>
            </div>
            <!-- Versión del selector de modelos para desktop -->
            <div class="hidden lg:block versions-desktop max-w-screen-lg mx-auto">
                <splide
                    class="w-11/12 xl:w-full mx-auto"
                    :slides="splideVersions"
                    :options="splideOptions"
                >
                    <splide-slide
                        v-for="(version, index) in splideVersions"
                        :key="index"
                    >
                        <div class="version p-4 bg-white mx-2 flex flex-wrap h-88">
                            <div class="ml-auto -mr-4">
                                <label class="checkbox-container">
                                    <input
                                        type="checkbox"
                                        @click="onCheckVersion(index)"
                                        :class="'check-' + index"
                                    >
                                    <span class="checkmark bg-white"></span>
                                </label>
                            </div>
                            <div class="w-full pl-4 leading-none flex flex-wrap content-center">
                                <h2 class="text-xl font-medium">{{ version.name }}</h2>
                                <p class="text-lg mt-3">${{ formatPrice(version.price) }} MXN</p>
                            </div>
                            <figure class="w-full h-40 flex justify-center items-center overflow-hidden">
                                <img
                                    width="312"
                                    height="140"
                                    class="w-full transform scale-150 origin-center"
                                    :src="version.colorImage"
                                    :data-splide-lazy="version.colorImage"
                                    :alt="version.name"
                                >
                            </figure>
                        </div>
                    </splide-slide>
                </splide>
            </div>
            <div class="mt-10">
                <button
                    class="block w-64 sm:w-72 md:w-80 lg:w-40 text-sm sm:text-base text-center bg-dark-blue text-white py-3 mx-auto"
                    :class="{ 'bg-light-gray': versionsToCompare.length < 2 }"
                    @click="compareVersions()"
                    :disabled="versionsToCompare.length < 2"
                >
                    Comparar
                </button>
            </div>
        </div>
        <!-- Vista del comparador de versiones -->
        <div
            v-show="showComparator"
            class="comparator pt-8 pb-10 lg:w-10/12 mx-auto"
        >
            <h3 class="font-medium text-xl sm:text-2xl text-center">Comparando Modelos</h3>
            <div class="flex flex-wrap sm:flex-no-wrap justify-center mt-6">
                <!-- Muestra la 1er versión a comparar -->
                <div
                    v-if="versionsToCompare[0] !== undefined"
                    class="flex flex-wrap justify-center w-full"
                >
                    <div class="w-48 lg:w-full text-center">
                        <h2 class="text-xl sm:text-2xl font-medium leading-tight">
                            {{ versions[versionsToCompare[0]].name }}</h2>
                        <p class="text-lg mt-2 sm:mt-3">${{ formatPrice(versions[versionsToCompare[0]].price) }} MXN</p>
                    </div>
                    <div>
                        <img
                            class="h-40 xl:h-56 w-full object-cover"
                            :src="versions[versionsToCompare[0]].colorImage"
                            :alt="versions[versionsToCompare[0]].name"
                        >
                    </div>
                </div>
                <div class="hidden sm:block line border-solid border-l border-light-gray h-56 mt-4"></div>
                <!-- Contenedor para mostrar los specs distintos para dispositivos móviles -->
                <div
                    v-if="diffSpecs"
                    class="specs w-11/12 sm:hidden flex flex-wrap items-center overflow-y-auto h-72"
                >
                    <div
                        v-for="specIdx in diffSpecs"
                        :key="specIdx"
                        class="flex flex-wrap items-center bg-light-brown px-4 py-2 mb-2 leading-tight w-full"
                    >
                        <p class="w-full sm:w-auto text-center">{{ getSpecVal(leftSpecs[specIdx].value) }}</p>
                        <p class="w-full sm:w-auto text-center font-medium my-1">{{ leftSpecs[specIdx].name }}</p>
                        <p class="w-full sm:w-auto text-center">{{ getSpecVal(rightSpecs[specIdx].value) }}</p>
                    </div>
                </div>
                <!-- Muestra la 2da versión a comparar -->
                <div
                    v-if="versionsToCompare[1] !== undefined"
                    class="flex flex-wrap justify-center w-full mt-4 sm:mt-0"
                >
                    <div class="w-48 lg:w-full text-center">
                        <h2 class="text-xl sm:text-2xl font-medium leading-tight">
                            {{ versions[versionsToCompare[1]].name }}</h2>
                        <p class="text-lg mt-2 sm:mt-3">${{ formatPrice(versions[versionsToCompare[1]].price) }} MXN</p>
                    </div>
                    <div class="mt-2">
                        <img
                            class="h-40 xl:h-56 w-full object-cover"
                            :src="versions[versionsToCompare[1]].colorImage"
                            :alt="versions[versionsToCompare[1]].name"
                        >
                    </div>
                </div>
            </div>
            <!-- Contenedor para mostrar los specs distintos para desktop -->
            <div
                v-if="diffSpecs"
                class="specs w-11/12 mx-auto mt-8 hidden sm:block overflow-y-auto h-64"
            >
                <div
                    v-for="specIdx in diffSpecs"
                    :key="specIdx"
                    class="flex flex-wrap justify-between items-center bg-light-brown lg:bg-white px-4 py-2 mb-2 leading-tight"
                >
                    <p class="w-1/3 text-center">{{ getSpecVal(leftSpecs[specIdx].value) }}</p>
                    <p class="w-1/3 text-center font-medium my-1">{{ leftSpecs[specIdx].name }}</p>
                    <p class="w-1/3 text-center">{{ getSpecVal(rightSpecs[specIdx].value) }}</p>
                </div>
            </div>
            <div class="mt-8">
                <button
                    class="flex mx-auto cursor-pointer"
                    @click="resetComparator()"
                >
                    <img
                        class="w-4 mr-4"
                        :src="`${url}/images/icons/again_icon.png`"
                        alt="Comparar de nuevo"
                    >
                    <p class="text-light-blue text-lg">Comparar de nuevo</p>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { Splide, SplideSlide } from "@splidejs/vue-splide";
import "@splidejs/splide/dist/css/themes/splide-default.min.css";
export default {
    components: {
        Splide,
        SplideSlide,
    },
    props: ["versions", "imageModel"],
    data() {
        return {
            url: $(".url").text(),
            // Variable para almacenar las versiones a comparar
            versionsToCompare: [],
            // Variable para determinar si mostrar o no las versiones
            // disponibles para comparar
            showVersions: false,
            // Variable para determinar si mostrar o no el comparador
            // con las versiones seleccionadas
            showComparator: false,
            // Variable para las opciones del slider del comparador
            splideOptions: {
                type: "slide",
                perPage: 4,
                perMove: 1,
                pagination: false,
                autoHeight: false,
                lazyLoad: "sequential",
                arrows: this.versions.length > 4 ? true : false,
                classes: {
                    arrow: "splide__arrow splide-arrow",
                    prev: "splide__arrow--prev splide-arrow-prev",
                    next: "splide__arrow--next splide-arrow-next",
                },
            },
            // Variable para determinar el inice de la imagen
            // de la versión a mostrar en el comparador
            color: 0,
            // Variable para almacenar las versiones disponibles que
            // se pueden comparar
            splideVersions: [],
            // colors: []
        };
    },
    methods: {
        // Función para formatear los precios
        formatPrice(price, n, x) {
            let re =
                "\\d(?=(\\d{" + (x || 3) + "})+" + (n > 0 ? "\\." : "$") + ")";
            return parseInt(price)
                .toFixed(Math.max(0, ~~n))
                .replace(new RegExp(re, "g"), "$&,");
        },
        // Función para seleccionar las versiones a comparar
        onCheckVersion(versionId) {
            if (this.versionsToCompare.includes(versionId)) {
                this.versionsToCompare = this.versionsToCompare.filter(
                    (version) => version !== versionId
                );
                $(".versions-comparator .checkbox-container input").prop(
                    "disabled",
                    false
                );
                $(
                    ".versions-comparator .checkbox-container input.check-" +
                    versionId
                ).prop("checked", false);
                $(
                    ".versions-comparator .checkbox-container input.check-" +
                    versionId
                )
                    .parents(".version")
                    .removeClass("bg-brown");
                $(
                    ".versions-comparator .checkbox-container .checkmark"
                ).removeClass("bg-light-gray");
            } else if (this.versionsToCompare.length < 2) {
                this.versionsToCompare.push(versionId);
                $(
                    ".versions-comparator .checkbox-container input.check-" +
                    versionId
                )
                    .parents(".version")
                    .addClass("bg-brown");
            }

            if (this.versionsToCompare.length === 2) {
                $(".versions-comparator .checkbox-container input").prop(
                    "disabled",
                    true
                );
                $(
                    ".versions-comparator .checkbox-container .checkmark"
                ).addClass("bg-light-gray");
                $(
                    ".versions-comparator .checkbox-container input:checked"
                ).prop("disabled", false);
                $(".versions-comparator .checkbox-container input:checked")
                    .parent()
                    .find(".checkmark")
                    .removeClass("bg-light-gray");
            }
        },
        // Obtiene las imágenes de las versiones para el comparador
        getVersionsImage() {
            // Recorre el array de versiones para guardar la correspondiente imagen para el comparador
            for (let version of this.versions) {
                let i360idx = 11;
                if (version.name === "Ioniq Limited") i360idx = 18;
                // else if (version.id >= 39 && version.id <= 42) i360idx = 7
                // let randIdx = Math.floor(Math.random() * version.colors.length)
                // while (this.colors.indexOf(randIdx) > 0) {
                //     randIdx = Math.floor(Math.random() * version.colors.length)
                // }
                // this.colors.push(randIdx)
                // console.log(this.color, version.colors.length - 1, this.color > version.colors.length - 1);
                if (this.color > version.colors.length - 2) {
                    this.color = 0;
                } else {
                    this.color++;
                }
                version.colorImage = version.colors[this.color].images_360[i360idx];
                if (version.colorImage == undefined){
                    version.colorImage = this.imageModel
                }
            }
            this.splideVersions = [...this.versions];
            this.showVersions = true;
        },
        // Función para ocultar las versiones disponibles
        // y mostrar el comparador
        compareVersions() {
            this.showVersions = false;
            this.showComparator = true;
        },
        // Función para reestablecer el comparador de versiones
        resetComparator() {
            this.showComparator = false;
            this.showVersions = true;
            let version1 = this.versionsToCompare[0];
            let version2 = this.versionsToCompare[1];
            this.onCheckVersion(version1);
            this.onCheckVersion(version2);
        },
        // Función para obtener las diferencias entre specs de los
        // modelos comparados
        getSpecVal(specValue) {
            if (specValue.trim() === "true") return "Sí";
            else if (specValue.trim() === "false") return "No";
            else return specValue.trim();
        },
    },
    computed: {
        // Retorna las specs de la primer versión a comparar
        leftSpecs() {
            if (this.versionsToCompare.length === 2) {
                return this.versions[this.versionsToCompare[0]].specs;
            }
            return null;
        },
        // Retorna las specs de la segunda versión a comparar
        rightSpecs() {
            if (this.versionsToCompare.length === 2) {
                return this.versions[this.versionsToCompare[1]].specs;
            }
            return null;
        },
        // Retorna las specs distintas entre las versiones comparadas
        diffSpecs() {
            if (this.versionsToCompare.length === 2) {
                let diffIndex = [];
                this.leftSpecs.forEach((spec, index) => {
                    if (spec.value !== this.rightSpecs[index].value)
                        diffIndex.push(index);
                });
                return diffIndex;
            }
            return null;
        },
    },
    mounted() {
        this.getVersionsImage();
    },
};
</script>

<style lang="scss">
.versions-comparator {

    /* Customize the label (the container) */
    .checkbox-container {
        display: block;
        position: relative;
        padding-left: 2.5rem;
        /* margin-bottom: 12px; */
        cursor: pointer;
        /* font-size: 22px; */
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }

    /* Hide the browser's default checkbox */
    .checkbox-container input {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    /* Create a custom checkbox */
    .checkmark {
        position: absolute;
        top: 0;
        left: 0;
        height: 1.5rem;
        width: 1.5rem;
        border: 1px solid #d8d8d8;
    }

    /* Create the checkmark/indicator (hidden when not checked) */
    .checkmark:after {
        content: "";
        position: absolute;
        display: none;
    }

    /* Show the checkmark when checked */
    .checkbox-container input:checked~.checkmark:after {
        display: block;
    }

    /* Style the checkmark/indicator */
    .checkbox-container .checkmark:after {
        left: 7px;
        top: 1px;
        width: 10px;
        height: 18px;
        border: solid #00aad2;
        border-width: 0 4px 4px 0;
        -webkit-transform: rotate(45deg);
        -ms-transform: rotate(45deg);
        transform: rotate(45deg);
    }

    .splide__list {
        margin: 0 auto !important;
    }

    .splide-arrow {
        opacity: 1;
        background: none;
    }

    .splide-arrow-prev {
        left: -2rem;
    }

    .splide-arrow-next {
        right: -2rem;
    }
}
</style>